import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Phone } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { serviceCatalog } from "@/data/services";
import { galleryImages } from "@/data/projectImages";

const ServiceDetail = () => {
  const { slug } = useParams();
  const { t } = useT();

  const service = serviceCatalog.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/dienstleistungen" replace />;

  const Icon = service.icon;
  const item = t.services.items[service.key];

  // Related gallery images by category mapping
  const categoryMap = {
    malerarbeiten: "innen",
    tapezier: "spezial",
    fassaden: "aussen",
    daemmung: "aussen",
    holzmalerei: "holz",
    stuck: "spezial",
    trockenbau: "innen",
    putz: "innen",
    spritz: "holz",
  };
  const relatedCategory = categoryMap[service.slug] || "innen";
  const related = galleryImages.filter((i) => i.category === relatedCategory).slice(0, 6);

  return (
    <div data-testid={`service-detail-${slug}`}>
      <section className="border-b border-zinc-200 bg-zinc-50 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/dienstleistungen" className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-red-600 transition-colors" data-testid="back-to-services">
            <ArrowLeft size={14} /> {t.nav.services}
          </Link>
          <div className="mt-6 sm:mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 items-start">
            <div className="lg:col-span-7">
              <Icon size={40} className="text-red-600" strokeWidth={1.5} />
              <h1 className="mt-5 sm:mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-extrabold tracking-tighter text-zinc-900">
                {item.title}
              </h1>
              <p className="mt-4 sm:mt-6 text-base sm:text-lg text-zinc-600 leading-relaxed">{item.short}</p>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-zinc-100 to-zinc-200 border border-zinc-200 relative">
                {service.image ? (
                  <img src={service.image} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icon size={120} className="text-zinc-300" strokeWidth={1} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {item.features?.length > 0 && (
        <section className="py-14 sm:py-20 border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-overline">{t.services.detail_intro}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-display font-bold tracking-tighter text-zinc-900 max-w-3xl">
              {item.title} – {item.short}
            </h2>
            <div className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 sm:gap-x-10 gap-y-4 sm:gap-y-5">
              {item.features.map((f, i) => (
                <div key={i} className="flex items-start gap-3 border-t border-zinc-200 pt-4" data-testid={`feature-${i}`}>
                  <Check size={18} className="text-red-600 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-sm text-zinc-800 font-medium">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="py-14 sm:py-20 border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-overline">{t.gallery.overline}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-display font-bold tracking-tighter text-zinc-900">
              {t.gallery.title}
            </h2>
            <div className="mt-8 sm:mt-12 grid grid-cols-2 md:grid-cols-3 gap-3">
              {related.map((img) => (
                <div key={img.id} className="aspect-[4/3] overflow-hidden bg-zinc-100" data-testid={`related-${img.id}`}>
                  <img src={img.url} alt={img.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-14 sm:py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tighter">
            {t.services.detail_cta_title}
          </h2>
          <p className="mt-5 text-base text-red-50 leading-relaxed max-w-2xl mx-auto">
            {t.services.detail_cta_text}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/kontakt" className="bg-white text-red-600 px-7 py-4 font-bold inline-flex items-center gap-2 hover:bg-zinc-100 transition-colors" data-testid="detail-cta-contact">
              {t.common.cta_offer} <ArrowRight size={16} />
            </Link>
            <a href="tel:+41796070148" className="border-2 border-white text-white px-7 py-4 font-bold inline-flex items-center gap-2 hover:bg-white hover:text-red-600 transition-colors" data-testid="detail-cta-call">
              <Phone size={16} /> {t.common.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
